/**
 * Safe Component Wrapper - Provides error boundaries and fallbacks for components
 */

import React, { Component, ReactNode, Suspense } from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface SafeComponentProps {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

class ComponentErrorBoundary extends Component<SafeComponentProps, ErrorBoundaryState> {
  constructor(props: SafeComponentProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`❌ Component Error in ${this.props.componentName || 'Unknown'}:`, error);
    console.error('Error Info:', errorInfo);

    this.setState({ error, errorInfo });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-100 mb-2">
            ⚠️ Component Error
          </h3>
          <p className="text-red-700 dark:text-red-200 mb-2">
            The {this.props.componentName || 'component'} failed to load properly.
          </p>
          <details className="text-sm">
            <summary className="cursor-pointer text-red-600 dark:text-red-300 hover:text-red-800 dark:hover:text-red-100 font-medium">
              Show Error Details
            </summary>
            <div className="mt-2 p-2 bg-red-100 dark:bg-red-950/50 rounded font-mono text-xs text-red-900 dark:text-red-100">
              <div>
                <strong>Error:</strong> {this.state.error?.message}
              </div>
              {this.state.errorInfo && (
                <div className="mt-1">
                  <strong>Stack:</strong>
                  <pre className="whitespace-pre-wrap text-red-800 dark:text-red-200">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </div>
          </details>
          <button
            onClick={() =>
              this.setState({
                hasError: false,
                error: undefined,
                errorInfo: undefined
              })
            }
            className="mt-3 px-3 py-1 bg-red-600 dark:bg-red-700 text-white rounded text-sm hover:bg-red-700 dark:hover:bg-red-800 transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Loading fallback component
function LoadingFallback({ componentName }: { componentName?: string }) {
  return (
    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <div className="flex items-center space-x-3">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-blue-700 dark:text-blue-300">
          Loading {componentName || 'component'}...
        </span>
      </div>
    </div>
  );
}

// Feature disabled fallback
function FeatureDisabledFallback({ featureName }: { featureName: string }) {
  return (
    <div className="p-4 bg-surface-alt border border-line rounded-lg">
      <div className="text-center">
        <div className="text-2xl mb-2">🚧</div>
        <h3 className="text-lg font-semibold text-ink mb-2">Feature Disabled</h3>
        <p className="text-ink-dim text-sm">
          {featureName} is currently disabled. Enable it in the debug panel to access this feature.
        </p>
      </div>
    </div>
  );
}

// Main SafeComponent wrapper
interface SafeComponentWrapperProps {
  children: ReactNode;
  componentName: string;
  loadingFallback?: ReactNode;
  errorFallback?: ReactNode;
}

export function SafeComponent({
  children,
  componentName,
  loadingFallback,
  errorFallback
}: SafeComponentWrapperProps) {
  return (
    <ComponentErrorBoundary componentName={componentName} fallback={errorFallback}>
      <Suspense fallback={loadingFallback || <LoadingFallback componentName={componentName} />}>
        {children}
      </Suspense>
    </ComponentErrorBoundary>
  );
}

// HOC for wrapping lazy components
export function withSafeLoading<P extends object>(
  LazyComponent: React.LazyExoticComponent<React.ComponentType<P>>,
  componentName: string
) {
  return function SafeWrappedComponent(props: P) {
    return (
      <SafeComponent componentName={componentName}>
        <LazyComponent {...(props as any)} />
      </SafeComponent>
    );
  };
}

export { LoadingFallback, FeatureDisabledFallback };
